import { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Card, CardContent, IconButton, Tab, Tabs } from '@mui/material';
import HomeIcon from '@mui/icons-material/Home';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useSetRecoilState } from 'recoil';
import ConfirmBox from '../../../../components/FlowBuilder/Common/ConfirmBox/index.tsx';
import DynamicBreadcrumbs from '../../../../components/Shared/Breadcrumb/index.tsx';
import TemplateSendDialog from '../send/TemplateSendDialog';
import TemplateDetailsHeader from './TemplateDetailsHeader';
import TemplatePerformancePanel from './TemplatePerformancePanel';
import TemplateSendsPanel from './TemplateSendsPanel';
import TemplateActivityPanel from './TemplateActivityPanel';
import { TemplateDetailsContext } from '../../context/TemplateDetailsContext';
import useTemplateDetailsActions from '../../hooks/useTemplateDetailsActions';
import useTemplatePerformance from '../../hooks/useTemplatePerformance';
import useTemplateSx from '../../hooks/useTemplateSx';
import { TEMPLATE_BASE_PATH } from '../../constants/templateApi';
import { templateSummaryFiltersAtom } from '../../state/templateAtoms';

const TAB_KEYS = ['performance',
   'insights',
    // 'activity'
  ];

function resolveTabIndex(tabParam) {
  if (tabParam === 'sends' || tabParam === 'summary' || tabParam === 'insights') return 1;
  // if (tabParam === 'activity') return 2;
  if (tabParam === 'performance' || tabParam === 'insights') return 0;
  return 0;
}

function a11yProps(index) {
  return {
    id: `template-tab-${index}`,
    'aria-controls': `template-tabpanel-${index}`,
  };
}

export default function TemplateSingleAnalyticsView() {
  const styles = useTemplateSx();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const setSummaryFilters = useSetRecoilState(templateSummaryFiltersAtom);
  const performance = useTemplatePerformance();
  const actions = useTemplateDetailsActions(performance.template);
  const [tab, setTab] = useState(() => resolveTabIndex(searchParams.get('tab')));

  useEffect(() => {
    setTab(resolveTabIndex(searchParams.get('tab')));
  }, [searchParams]);

  const navigateToTemplate = useCallback(
    (row, targetTab = 'performance') => {
      const name = row?.display_name ?? row?.template_name;
      const bot = row?.bot_number;
      if (!name || !bot || bot === '—') return;

      setSummaryFilters((prev) => ({
        ...prev,
        start_date: performance.filters.start_date ?? prev.start_date,
        end_date: performance.filters.end_date ?? prev.end_date,
      }));

      navigate(
        `${TEMPLATE_BASE_PATH}/template-details/${encodeURIComponent(name)}/${encodeURIComponent(bot)}?tab=${targetTab}`
      );
    },
    [navigate, performance.filters.end_date, performance.filters.start_date, setSummaryFilters]
  );

  const contextValue = useMemo(
    () => ({
      ...performance,
      ...actions,
      navigateToTemplate,
    }),
    [actions, navigateToTemplate, performance]
  );

  const breadcrumbLabel =
    tab === 0 ? 'Performance' : tab === 1 ? 'Insights' : 'Activity';

  const breadcrumbsData = [
    { label: <IconButton color="primary"><HomeIcon /></IconButton>, href: '/' },
    { label: 'Templates', href: `${TEMPLATE_BASE_PATH}/template-list` },
    {
      label: performance.isOrgMode
        ? 'Performance Reports'
        : (performance.templateName ?? breadcrumbLabel),
    },
  ];

  return (
    <TemplateDetailsContext.Provider value={contextValue}>
      <Box sx={styles.pageRoot}>
        <DynamicBreadcrumbs
          items={breadcrumbsData}
          onBreadcrumbClick={(_event, item) => {
            if (item?.href) navigate(item.href);
          }}
        />

        <TemplateDetailsHeader />

        <Card sx={{ mb: 2 }}>
          <CardContent sx={{ pb: '0 !important' }}>
            <Tabs
              value={tab}
              onChange={(_event, value) => {
                setTab(value);
                const key = TAB_KEYS[value] ?? 'performance';
                navigate(`?tab=${key}`, { replace: true });
              }}
            >
              <Tab label="Performance" {...a11yProps(0)} />
              <Tab label="Insights" {...a11yProps(1)} />
              {/* <Tab label="Activity" {...a11yProps(2)} /> */}
            </Tabs>
          </CardContent>
        </Card>

        {tab === 0 ? <TemplatePerformancePanel /> : null}
        {tab === 1 ? <TemplateSendsPanel /> : null}
        {/* {tab === 2 ? <TemplateActivityPanel /> : null} */}

        {!performance.isOrgMode ? (
          <>
            <ConfirmBox
              open={actions.confirmDeleteOpen}
              onClose={actions.onConfirmDelete}
              options={{
                title: `Delete ${performance.templateName ?? 'Template'}`,
                text: 'This will permanently delete the template from Meta and cannot be undone.',
                confirmButtonText: 'Yes',
                cancelButtonText: 'Cancel',
                confirmButtonColor: 'error',
              }}
            />
            <TemplateSendDialog />
          </>
        ) : null}
      </Box>
    </TemplateDetailsContext.Provider>
  );
}
